/**
 * Dates, weeks and durations.
 *
 * Two decisions run through this whole file.
 *
 * 1. A working day is a CALENDAR day, not an instant. "Monday the 3rd" is the same
 *    day for everyone looking at the timesheet, so days are carried as `YYYY-MM-DD`
 *    strings and all arithmetic is done in UTC. Doing it in local time means the
 *    server's timezone can shift which week an entry falls in - the class of bug
 *    that puts an entry in a week the user has already submitted.
 *
 * 2. Durations are integer SECONDS. the legacy system stores seconds, the migration carries
 *    them across unchanged, and float hours cannot represent 7h20m exactly. Every
 *    total in this app is a sum of integers; formatting to `7:20` happens at the
 *    edge, once.
 */

/** A calendar day, `YYYY-MM-DD`. */
export type DateOnly = string;

const DAY_MS = 86_400_000;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function isDateOnly(value: unknown): value is DateOnly {
  if (typeof value !== 'string' || !DATE_ONLY.test(value)) return false;
  // Rejects 2026-02-30: Date normalises it to March, so the round-trip disagrees.
  return toDateOnly(parseDateOnly(value)) === value;
}

export function parseDateOnly(day: DateOnly): Date {
  return new Date(`${day}T00:00:00.000Z`);
}

export function toDateOnly(date: Date): DateOnly {
  return date.toISOString().slice(0, 10);
}

/**
 * Today's calendar date in a given timezone.
 *
 * `toDateOnly(new Date())` is UTC today, which is NOT today for anybody east or
 * west of it: in Asia/Kolkata (UTC+5:30) every moment between midnight and 05:30
 * local is still the previous day in UTC. That would highlight the wrong day on the
 * week grid, aim a new entry at yesterday, and shift the "yesterday" lockdown
 * default by a day for anybody working early.
 *
 * Setting TZ on the container does not fix it - `toISOString()` is defined to be
 * UTC - so the zone has to be applied explicitly. `en-CA` is used because it
 * formats as YYYY-MM-DD, which is the shape this whole app keys on.
 */
export function todayIn(timeZone: string): DateOnly {
  return dateIn(new Date(), timeZone);
}

/** The wall-clock time an instant shows in `timeZone`, as `HH:MM`. */
export function clockIn(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
}

/** The calendar day an instant falls on in `timeZone`. */
export function dateIn(instant: Date, timeZone: string): DateOnly {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

export function addDays(day: DateOnly, count: number): DateOnly {
  return toDateOnly(new Date(parseDateOnly(day).getTime() + count * DAY_MS));
}

/** Whole days from `from` to `to`, inclusive of both ends. */
export function daysBetweenInclusive(from: DateOnly, to: DateOnly): number {
  const span = parseDateOnly(to).getTime() - parseDateOnly(from).getTime();
  return Math.floor(span / DAY_MS) + 1;
}

/* ------------------------------------------------------------------ weeks -- */

/**
 * The Monday of the week containing `day`.
 *
 * Weeks are ISO Monday-Sunday because that is what the approval cycle already is:
 * every one of the 1,384 approved weeks in the legacy database is keyed to an ISO
 * Monday, and re-basing them to a different first day would invalidate the lot.
 */
export function startOfWeek(day: DateOnly): DateOnly {
  const date = parseDateOnly(day);
  // getUTCDay(): 0 = Sunday. Shift so Monday is 0 and Sunday is 6.
  const offset = (date.getUTCDay() + 6) % 7;
  return addDays(day, -offset);
}

export function endOfWeek(day: DateOnly): DateOnly {
  return addDays(startOfWeek(day), 6);
}

/** The seven days of the week containing `day`, Monday first. */
export function weekDays(day: DateOnly): DateOnly[] {
  const monday = startOfWeek(day);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/* ----------------------------------------------------------------- months -- */

export function startOfMonth(day: DateOnly): DateOnly {
  return `${day.slice(0, 7)}-01`;
}

export function endOfMonth(day: DateOnly): DateOnly {
  const date = parseDateOnly(day);
  // Day 0 of the next month is the last day of this one.
  return toDateOnly(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)));
}

export function sameMonth(a: DateOnly, b: DateOnly): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/* --------------------------------------------------------- financial year -- */

/**
 * A financial year: its range, and the twelve month columns it covers.
 *
 * The organisation's year does not start in January. The previous system carried
 * `company.financial_year: 2026-04-01` - India's tax year, April to March - and
 * reporting on 1 January to 31 December instead resets every overtime balance
 * three months early. That is arithmetically fine and is not the year anybody
 * here is measured against.
 *
 * `months` are the columns in order as `YYYY-MM`, and a day's month index IS its
 * position in that list. Callers must not compute one themselves: a report that
 * derives its own index disagrees with its own headers the moment the start month
 * changes, and nothing in the numbers looks wrong.
 *
 * `startMonth` 1 gives the calendar year back unchanged, so this turns off by
 * setting one number rather than by reverting code.
 */
export function financialYear(
  year: number,
  startMonth: number,
): { from: DateOnly; to: DateOnly; months: string[]; label: string } {
  const months = Array.from({ length: 12 }, (_, i) => {
    const offset = startMonth - 1 + i;
    const y = year + Math.floor(offset / 12);
    return `${y}-${String((offset % 12) + 1).padStart(2, '0')}`;
  });
  return {
    from: `${months[0]}-01`,
    to: endOfMonth(`${months[11]}-01`),
    months,
    // "2026-27" says which year is meant where a bare "2026" would be read as
    // January to December by everybody who has not been told otherwise.
    label: startMonth === 1 ? String(year) : `${year}-${String((year + 1) % 100).padStart(2, '0')}`,
  };
}

/** The financial year a day belongs to. */
export function financialYearOf(day: DateOnly, startMonth: number): number {
  const year = Number(day.slice(0, 4));
  return Number(day.slice(5, 7)) >= startMonth ? year : year - 1;
}

/* -------------------------------------------------------------- durations -- */

/**
 * Seconds as `H:MM`, the form the timesheet shows everywhere.
 *
 * Hours are NOT wrapped at 24: a weekly total of 41:30 has to read as 41:30, not
 * as 17:30 into some second day.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * The longest a single entry may be. A day cannot be longer than a day, and the
 * typo this catches is a real one: a bare number is hours, so "30" asks for thirty
 * of them rather than half an hour.
 *
 * Here rather than in each service because all four write paths refuse it and the
 * grid marks it before saving - five copies of `24 * 3600` are five chances for one
 * of them to drift.
 */
export const MAX_ENTRY_SECONDS = 24 * 3600;

/**
 * Parse what people actually type into a duration box: `7:30`, `7.5`, `7h30m`,
 * `90m`, `7`. Returns null for anything it cannot read, so the caller shows a
 * validation message rather than silently recording zero.
 */
export function parseDuration(input: string): number | null {
  const text = input.trim().toLowerCase();
  if (text === '') return null;

  // Minutes need not be padded - "9:5" is five past nine o'clock's worth, not a
  // typo to refuse - and seconds are accepted because the previous system accepted
  // them and 1,414 descriptions' worth of habits came across with the people.
  // Unlike that system, 60 or more minutes is still refused rather than carried:
  // it reinterpreted "8:75" as 9h15m, and silently agreeing with a typo about how
  // long somebody worked is worse than asking.
  const clock = /^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/.exec(text);
  if (clock) {
    return Number(clock[1]) * 3600 + Number(clock[2]) * 60 + Number(clock[3] ?? 0);
  }

  const hm = /^(?:(\d+(?:\.\d+)?)h)?\s*(?:(\d+)m)?$/.exec(text);
  if (hm && (hm[1] !== undefined || hm[2] !== undefined)) {
    const hours = hm[1] === undefined ? 0 : Number(hm[1]);
    const mins = hm[2] === undefined ? 0 : Number(hm[2]);
    return Math.round(hours * 3600 + mins * 60);
  }

  // A bare number is hours - "7" means seven hours, not seven minutes. It is the
  // last rule tried so "90m" is never read as 90 hours.
  const bare = /^\d+(?:\.\d+)?$/.exec(text);
  if (bare) return Math.round(Number(text) * 3600);

  return null;
}

/**
 * Rewrite what somebody typed as `H:MM`, so a field shows what was understood
 * before the save rather than after it: "7.5" becomes "7:30", "90m" becomes "1:30".
 *
 * Text that cannot be read is returned untouched. The caller marks it and refuses
 * the save, and replacing it would throw away the very thing being corrected.
 */
export function normalizeDuration(input: string): string {
  const seconds = parseDuration(input);
  return seconds === null ? input : formatDuration(seconds);
}

/**
 * Minutes that `zone` is ahead of UTC at `instant`.
 *
 * Uses Intl rather than a timezone library: the offset has to be correct for the
 * specific date, and the platform already carries the tz database.
 */
export function zoneOffsetMinutes(instant: Date, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const at = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  const asUtc = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour') % 24, at('minute'), at('second'));
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * The instant an entry starts: a calendar day plus a wall-clock time, read in the
 * employee's own zone.
 *
 * Here rather than in a service because the day form and the week grid both create
 * entries and both have to place them at the same moment. They did not: the grid
 * pinned every entry it created to 10:00 UTC, so the same person's hours started at
 * 15:30 if they came from the grid and at the configured 10:00 if they came from
 * the day form, and the day list showed both.
 *
 * `hhmm` is 24-hour "HH:MM". The offset is resolved for that specific date, so a
 * zone with DST lands on the right moment rather than a fixed offset captured once.
 */
export function beginInstant(day: DateOnly, hhmm: string, timeZone: string): Date {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(hhmm)) {
    throw new Error(`Start time must be HH:MM, got "${hhmm}".`);
  }
  const naive = new Date(`${day}T${hhmm}:00.000Z`);
  return new Date(naive.getTime() - zoneOffsetMinutes(naive, timeZone) * 60_000);
}
